// kiron/quiron/frontend/src/components/LibraryDetail.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getLibraryDocuments, uploadDocumentToLibrary, deleteDocument, API_STATIC_URL } from '../api';
import { useNotification } from '../contexts/NotificationContext';
import styles from './LibraryDetail.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFileUpload, faTrash, faSpinner, faCheckCircle, faExclamationTriangle, faClock, faComments } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from './ConfirmationModal';
import InlineLoading from './InlineLoading';
import DocumentViewerModal from './DocumentViewerModal';

const LibraryDetail = ({ library, onBack, onStartChat }) => {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const fileInputRef = useRef(null);

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const fetchDocuments = useCallback(async () => {
    // Não precisa setar isLoading aqui para evitar piscar na atualização automática
    try {
      const data = await getLibraryDocuments(library.id);
      setDocuments(data);
    } catch (error) {
      addNotification(t('errorLoadingDocuments'), 'error');
    } finally {
      setIsLoading(false); // Garante que o loading inicial termine
    }
  }, [library.id, addNotification, t]);

  useEffect(() => {
    if (!isViewerOpen) {
      fetchDocuments();
      const interval = setInterval(fetchDocuments, 20000);
      return () => clearInterval(interval);
    }
  }, [fetchDocuments, isViewerOpen]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await uploadDocumentToLibrary(library.id, file);
      addNotification(t('uploadStartedSuccess'), 'success');
      setIsLoading(true); // Mostra o loading enquanto o novo doc não aparece
      fetchDocuments();
    } catch (error) {
      addNotification(error.message || t('errorUploadingDocument'), 'error');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteClick = (e, doc) => {
      e.stopPropagation();
      setDocToDelete(doc);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    try {
      await deleteDocument(library.id, docToDelete.id);
      addNotification(t('documentDeletedSuccess'), 'success');
      setDocToDelete(null);
      fetchDocuments();
    } catch (error) {
      addNotification(error.message || t('errorDeletingDocument'), 'error');
      setDocToDelete(null);
    }
  };

  const handleViewDocument = (doc) => {
    if (doc.status === 'processed' && doc.storage_path && doc.storage_path.toLowerCase().endsWith('.pdf')) {
        setSelectedDoc(doc);
        setIsViewerOpen(true);
    } else if (doc.status !== 'processed') {
        addNotification(t('documentNotProcessedYet'), 'info');
    } else {
        addNotification(t('previewOnlyForPdf'), 'info');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processed': return <FontAwesomeIcon icon={faCheckCircle} className={styles.statusProcessed} title={t('processed')} />;
      case 'processing': return <FontAwesomeIcon icon={faSpinner} spin className={styles.statusProcessing} title={t('processing')} />;
      case 'pending': return <FontAwesomeIcon icon={faClock} className={styles.statusPending} title={t('pending')} />;
      case 'error': return <FontAwesomeIcon icon={faExclamationTriangle} className={styles.statusError} title={t('error')} />;
      default: return null;
    }
  };

  // Função auxiliar para renderizar o conteúdo principal de forma limpa
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.statusContainer}>
          <InlineLoading text={t('loadingDocuments')} />
        </div>
      );
    }

    if (documents.length === 0) {
      return (
        <div className={styles.statusContainer}>
          <p>{t('noDocumentsInLibrary')}</p>
        </div>
      );
    }

    return (
      <div className={styles.documentGrid}>
        {documents.map(doc => (
          <div key={doc.id} className={styles.documentCard} onClick={() => handleViewDocument(doc)}>
            <div className={styles.cardThumbnail}>
              {doc.thumbnail_filename ? (
                <img src={`${API_STATIC_URL}/api/academic/thumbnails/${doc.thumbnail_filename}`} alt={doc.original_filename.replace(/\.[^/.]+$/, "")} />
              ) : (
                <div className={styles.thumbnailPlaceholder}>
                  <span>{doc.original_filename.split('.').pop()}</span>
                </div>
              )}
            </div>
            <div className={styles.cardOverlay}>
              <div className={styles.cardStatus} title={t(doc.status)}>
                {getStatusIcon(doc.status)}
              </div>
              <button onClick={(e) => handleDeleteClick(e, doc)} className={styles.cardDeleteButton} title={t('deleteDocument')}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
            <div className={styles.cardFooter}>
              <span className={styles.cardTitle}>{doc.original_filename.replace(/\.[^/.]+$/, "")}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.detailContainer}>
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backButton}>
          <FontAwesomeIcon icon={faArrowLeft} /> {t('backToLibraries')}
        </button>
        <h3 className={styles.title}>{library.name}</h3>
        <div className={styles.headerActions}>
          <button onClick={() => fileInputRef.current.click()} className={styles.uploadButton} disabled={isUploading}>
            <FontAwesomeIcon icon={faFileUpload} /> {isUploading ? t('uploading') : t('addDocument')}
          </button>
          <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
          <button onClick={() => onStartChat(library)} className={styles.chatButton}>
            <FontAwesomeIcon icon={faComments} /> {t('chatWithLibraryBtn')}
          </button>
        </div>
      </div>

      {/* Chamada da nova função de renderização */}
      {renderContent()}

      <ConfirmationModal
        isOpen={!!docToDelete}
        onClose={() => setDocToDelete(null)}
        onConfirm={confirmDelete}
        title={t('deleteDocumentConfirmationTitle')}
        message={t('deleteDocumentConfirmationMessage', { name: docToDelete?.original_filename })}
        confirmButtonText={t('delete')}
        cancelButtonText={t('cancel')}
      />

      <DocumentViewerModal
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        doc={selectedDoc}
      />
    </div>
  );
};

export default LibraryDetail;